import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { BREAKPOINT, LAYOUT, SPACING } from '../constants/constants';

/**
 * 본문 폭(`.ct-page` 의 안쪽 폭) — 부품 안의 폭 판단(표 열 세트 · 버튼 쌓기 · 옆 패널)은 화면 폭이 아니라 이것으로 한다(디자인 시스템 v2
 * §2-1, 감사 card-controltower#834). 본문에 최대 폭(1440)이 생겨 "화면 폭 − 사이드바 220 − 여백 48" 손계산이 넓은 화면에서 틀린다.
 *
 * 값은 DashboardLayout 이 `.ct-page` 를 ResizeObserver 로 재서 넣는다(앱에 본문 상자는 한 번에 하나). 화면이 DashboardLayout 을 자기
 * 안에서 그리므로(화면 함수 본문은 레이아웃 바깥) React 컨텍스트가 아니라 모듈 저장소로 둔다.
 */

let measured: number | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** DashboardLayout 전용 — 잰 본문 폭(사라지면 null). */
export function setMeasuredContentWidth(width: number | null): void {
  const next = width == null ? null : Math.round(width);
  if (next === measured) return;
  measured = next;
  emit();
}

/** 아직 재지 못했을 때(첫 그리기 · 본문 상자 밖)의 짐작 — 화면 폭에서 사이드바 · 여백을 빼고 최대 폭으로 자른다. */
export function estimateContentWidth(viewport: number = typeof window !== 'undefined' ? window.innerWidth : 1280): number {
  const desktop = viewport >= BREAKPOINT.desktop;
  const side = desktop ? LAYOUT.sidebarWidth : 0;
  const pad = (desktop ? SPACING.xl : SPACING.md) * 2;
  return Math.max(0, Math.min(viewport - side - pad, LAYOUT.contentMaxWidth));
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  if (typeof window !== 'undefined') window.addEventListener('resize', cb);
  return () => {
    listeners.delete(cb);
    if (typeof window !== 'undefined') window.removeEventListener('resize', cb);
  };
};

/** 잰 값이 있는가(테스트 · 첫 그리기 판단용). */
export const hasMeasuredContentWidth = (): boolean => measured != null;

/** 본문 폭(px). 잰 값이 없으면 화면 폭으로 짐작한 값. */
export function useContentWidth(): number {
  return useSyncExternalStore(subscribe, () => measured ?? estimateContentWidth(), () => estimateContentWidth(1280));
}

/**
 * 본문 폭 조건 — 잰 값이 있으면 `predicate(본문 폭)`, 아직 없으면(첫 그리기 · 본문 상자를 그리지 않는 단위 테스트) 예전 화면 폭 질의
 * `legacyQuery` 를 그대로 쓴다. 예전 질의를 남겨 두는 이유: 화면 폭을 흉내 내는 테스트(matchMedia 목)가 그대로 돈다. 새 코드는
 * legacyQuery 없이 쓴다(짐작 값으로).
 */
export function useContentQuery(predicate: (width: number) => boolean, legacyQuery?: string): boolean {
  const width = useSyncExternalStore(subscribe, () => measured, () => null);
  const legacy = useLegacyQuery(legacyQuery);
  if (width != null) return predicate(width);
  if (legacyQuery && legacy != null) return legacy;
  return predicate(estimateContentWidth());
}

function useLegacyQuery(query?: string): boolean | null {
  const read = useCallback((): boolean | null => {
    if (!query || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia(query).matches;
  }, [query]);
  const [ok, setOk] = useState<boolean | null>(read);
  useEffect(() => {
    if (!query || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setOk(e.matches);
    setOk(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query, read]);
  return ok;
}

/** 테스트 전용 — 잰 값을 비운다. */
export const resetContentWidthForTest = (): void => { setMeasuredContentWidth(null); };
